module Visonum.BulkPingApp.MainPanel
  ( view
  ) where

import Visonum.BulkPingApp.Prelude

import Data.Array as Array
import Data.Date (Date)
import Data.Date (day, month, year) as D
import Data.DateTime as DT
import Data.Enum (fromEnum)
import Data.Int as Int
import Data.Map as Map
import Data.Maybe (Maybe(..))
import Data.Number.Format (fixed)
import Data.Number.Format as NumberFmt
import Data.String as Str
import Data.Time (Time)
import Data.Time as T
import Data.Traversable (fold)
import Data.Tuple.Nested (type (/\), (/\))
import Halogen.HTML.Elements as H
import Halogen.HTML.Elements.Keyed as HK
import Halogen.HTML.Events as HE
import Halogen.HTML.Properties as HP
import Visonum.API.SpeedMaster (BulkPingJobStatus(..), BulkPingJobStatusEntry, UUIDv4)
import Visonum.BulkPingApp.Components.App.Types (Action(..), State)
import Visonum.BulkPingApp.Imgs (imgCancelled, imgCross, imgExclamation, imgSuccess, imgWheel)
import Visonum.BulkPingApp.Util (padWith)

view :: forall w. State -> AppHTML w Action
view state =
  let
    elRoot = styleNode H.div
      "MainPanel"
      [ inline
          { display: "flex"
          , flexDirection: "row"
          , width: "100%"
          , padding: "15px"
          , justifyContent: "space-between"
          , fontFamily: "Arial"
          , fontSize: "12px"
          }
      ]

    elJobs = styleNodeKeyed HK.div
      "Jobs"
      [ inline
          { display: "flex"
          , flexDirection: "column"
          , width: "100%"
          , justifyContent: "space-between"
          , gap: "16px"
          }
      ]

    sortedJobs :: Array (UUIDv4 /\ BulkPingJobStatusEntry)
    sortedJobs = state.bulkPingStatus.jobs
      # Map.toUnfoldable
      # Array.sortBy (\(_ /\ v1) (_ /\ v2) -> compare v2.startedAt v1.startedAt)

  in
    elRoot
      []
      [ if Array.length sortedJobs == 0 then text "No Bulk Ping jobs available"
        else
          elJobs [] (map (\all@(k /\ _) -> k /\ viewJob state all) sortedJobs)
      ]

viewJob :: forall w. State -> (UUIDv4 /\ BulkPingJobStatusEntry) -> AppHTML w Action
viewJob state (uuid /\ entry) =
  let
    isSelected = case state.details of
      Just { jobId } -> jobId == uuid
      _ -> false

    isRunning = case entry.status of
      Running -> true
      _ -> false

    elRoot = styleNode H.div
      "Job"
      [ scopedSheet $ select ""
          { width: "100%"
          , height: "50px"
          , outline:
              if isSelected then "2px solid #65687d"
              else "1px solid #ACABAC"
          , borderRadius: "8px"
          , backgroundColor: case entry.status of
              Failed -> "#f0e0e0" <> if isSelected then "FF" else "88"
              Finished -> "#e0f0e0" <> if isSelected then "FF" else "88"
              Running -> "#d0d7ff" <> if isSelected then "FF" else "88"
              Cancelled -> "#f0f0e0" <> if isSelected then "FF" else "88"
              Pending -> "white"
          , transition: "background-color 0.1s"
          , fontSize: "12px"
          , fontFamily: "Arial"
          , display: "flex"
          , flexDirection: "row"
          }
      ]

    elMain = styleNode H.div
      "Main"
      [ scopedSheet $ select_
          { display: "grid"
          , width: "100%"
          , height: "100%"
          , borderRadius: "8px"
          , paddingRight: "10px"
          , gridTemplateColumns: "40px 80px 80px 90px auto"
          , gridTemplateRows: "1fr 1fr 1fr"
          , gridTemplateAreas: Str.joinWith " "
              [ "\"StatusIcon Empty       Empty       Empty       Empty      \""
              , "\"StatusIcon Date        Time        Progress    UUID       \""
              , "\"StatusIcon ProgressBar ProgressBar ProgressBar ProgressBar\""
              ]
          , fontSize: "12px"
          , fontWeight: if isSelected then "bold" else "normal"
          , fontFamily: "Arial"
          , cursor: "pointer"
          }
      , scopedSheet $ select ":hover"
          { fontWeight: "bold"
          }
      ]

    elEmpty = styleNode H.div
      "Empty"
      [ inline
          { gridArea: "Empty"
          }
      ]

    elStatusIcon = styleNode H.div
      "StatusIcon"
      [ inline
          { gridArea: "StatusIcon"
          , display: "flex"
          , justifyContent: "center"
          , alignItems: "center"
          }
      ]

    elDate = styleNode H.div
      "Date"
      [ inline
          { gridArea: "Date"
          , display: "flex"
          , justifyContent: "start"
          , alignItems: "center"
          , fontFeatureSettings: "\"tnum\""
          }
      ]

    elTime = styleNode H.div
      "Time"
      [ inline
          { gridArea: "Time"
          , display: "flex"
          , justifyContent: "start"
          , alignItems: "center"
          , fontFeatureSettings: "\"tnum\""
          }
      ]

    elUUID = styleNode H.div
      "UUID"
      [ inline
          { gridArea: "UUID"
          , display: "flex"
          , justifyContent: "start"
          , alignItems: "center"
          , fontFamily: "Monospace"
          , fontSize: "10px"
          , fontFeatureSettings: "\"tnum\""
          }
      ]

    elProgress = styleNode H.div
      "Progress"
      [ inline
          { gridArea: "Progress"
          , display: "flex"
          , justifyContent: "start"
          , alignItems: "center"
          , fontSize: "10px"
          , fontFeatureSettings: "\"tnum\""
          }
      ]

    elProgressBar = styleNode H.div
      "ProgressBar"
      [ inline
          { gridArea: "ProgressBar"
          , display: "flex"
          , justifyContent: "center"
          , alignItems: "center"
          }
      ]

    elActions = styleNode H.div
      "Cancel"
      [ inline
          { display: "flex"
          , justifyContent: "center"
          , alignItems: "start"
          , backgroundColor: "rgba(255, 255, 255, 0.5)"
          , marginLeft: "5px"
          , padding: "5px"
          }
      ]

    elCancel = styleLeaf H.img
      "Cancel"
      [ inline
          { cursor: "pointer"
          , width: "20px"
          , height: "20px"
          }
      ]

    elImg = styleLeaf H.img
      "Img"
      [ inline
          { width: "20px"
          , height: "20px"
          }
      , case entry.status of
          Running -> fold
            [ scopedSheet $ animation
                [ step "from"
                    { transform: "rotate(0deg)"
                    }
                , step "to"
                    { transform: "rotate(359deg)"
                    }
                ]
            , ( scopedSheet $ select_
                  { animation: "3s linear infinite"
                  }
              )
            ]
          _ -> mempty

      ]

  in
    elRoot
      [ HE.onClick $ const $ ShowDetails uuid ]
      [ elMain []
          [ elEmpty [] []
          , elStatusIcon []
              [ elImg $
                  case entry.status of
                    Running ->
                      [ HP.src imgWheel ]
                    Finished ->
                      [ HP.src imgSuccess ]
                    Failed ->
                      [ HP.src imgExclamation ]
                    Cancelled ->
                      [ HP.src imgCancelled ]
                    Pending ->
                      []
              ]
          , elDate [] [ text $ printDate $ DT.date entry.startedAt ]
          , elTime [] [ text $ printTime $ DT.time entry.startedAt ]
          , elUUID [] [ text uuid ]
          , elProgress []
              [ text $ show entry.processedIpCount <> "/" <> show entry.initialIpCount ]
          , elProgressBar []
              [ viewProgressBar entry.processedIpCount entry.initialIpCount
              ]
          ]
      , if isSelected && isRunning then elActions
          [ HE.onClick $ const $ CancelJob uuid ]
          [ elCancel [ HP.src imgCross ]
          ]
        else text ""
      ]

viewProgressBar :: forall w. Int -> Int -> AppHTML w Action
viewProgressBar part whole =
  let
    percent = 100.0 * (Int.toNumber part / Int.toNumber whole)
    percentStr = NumberFmt.toStringWith (fixed 1) percent

    elRoot = styleNode H.div
      "ProgressBar"
      [ inline
          { display: "flex"
          , width: "100%"
          , height: "3px"
          , backgroundColor: "rgba(255, 255, 255, 0.5);"
          , alignItems: "center"
          , justifyContent: "center"
          , position: "relative"
          , borderRadius: "2px"
          , overflow: "hidden"
          , opacity: if part == whole then "0.0" else "1"
          }
      ]

    elBar = styleNode H.div
      "Bar"
      [ inline
          { height: "100%"
          , position: "absolute"
          , backgroundColor: "black"
          , left: "0"
          , width: percentStr <> "%"
          , transition: "width 1000ms linear"
          , opacity: "0.4"
          }
      ]
  in
    elRoot
      []
      [ elBar [] []
      ]

printDate :: Date -> String
printDate date =
  padWith 4 (show (fromEnum $ D.year date))
    <> "/"
    <> padWith 2 (show (fromEnum $ D.month date))
    <> "/"
    <> padWith 2 (show (fromEnum $ D.day date))

printTime :: Time -> String
printTime time =
  padWith 2 (show (fromEnum $ T.hour time))
    <> ":"
    <> padWith 2 (show (fromEnum $ T.minute time))
    <> ":"
    <> padWith 2 (show (fromEnum $ T.second time))

